/**
 * SPX-044 news rail — the RENDER-STATE seams, pure and testable outside Svelte.
 *
 * ============================================================================
 * ⚖ CAPSULE §16, VERBATIM AND NOT NEGOTIABLE: *NEWS NEVER FIRES A TRADE — IT CLASSIFIES
 * LIQUIDITY.* This module classifies FEED STATES; it computes no score, no signal, no level.
 * The panel renders headline/source/is_major/age and NOTHING that reads as a trade prompt.
 * ============================================================================
 *
 * THREE HONEST ABSENCES, ALL DISTINCT — the route serves them distinctly and this module
 * must never collapse them, because each one is a different claim about the world:
 *
 *   `no-snapshot`  the route's 404: NO capture row exists strictly before T. A claim about
 *                  OUR CAPTURE LANE ("we could not have known anything at T").
 *   `empty`        a 200 with count 0: the snapshot EXISTS and zero headlines pass the
 *                  relevance filter. A claim about THE NEWS ("a quiet window"). The yield
 *                  that makes quiet NORMAL is DAY-CLASS DEPENDENT and is served per
 *                  request (`measured_yield_applicable`); no figure is quoted here.
 *   STALE          a 200 whose snapshot is over the 120s budget: we HAVE news, but it is
 *                  old, and the chip must be LOUD about it (clause (4) of SPX-044 — the
 *                  route's stale COMPUTATION was gate-verified; the arms on this module
 *                  drive the RENDER half).
 *
 * Merging any two of these renders a lie: 404-as-empty reads a capture outage as a quiet
 * news day (the exact hazard news.ts names in its header), and empty-as-error cries wolf
 * ~daily on a healthy sparse filter.
 */

/**
 * ⛔ SEVERITY IS **OURS**, NOT THE VENDOR'S — and the label must say so wherever it renders.
 *
 * The route's own `severity_basis`, verbatim: *"OURS, not the vendor's: source weight
 * (measured macro-text rate) + macro keyword class + index ticker. STOPGAP until BABEL;
 * `is_major` is NOT an input."*
 *
 * This repo's #1 historical failure is a label that outlived its measurement (`CVD (session)`
 * on a chain that never resets, killed this afternoon). A computed score rendered bare next
 * to vendor headlines reads as the vendor's judgement. ***It is ours, it is a stopgap, and
 * the surface says both.***
 *
 * `band` is an OPEN arm (`CvdState` precedent): an unheard band renders as its own words
 * rather than becoming a compile error or being silently bucketed into a known one.
 */
export type SeverityBand = "low" | "medium" | "high" | (string & {});

export interface NewsSeverity {
  score: number;
  band: SeverityBand;
  /** The score's addends, served so the number is auditable rather than asserted. */
  parts: { source: number; macro: number; index: number };
  basis: string;
}

/** Mirror of the route's `NewsItem` (workers/read-api/src/news.ts). */
export interface NewsItem {
  headline: string;
  source: string;
  tickers: string[];
  is_major: boolean;
  sentiment: string;
  created_at: string;
  tags: string[];
  /**
   * OPTIONAL BY CONTRACT, and the optionality is load-bearing: the producer shipped
   * severity at 16:44Z and a client must tolerate a route that predates it (u's law —
   * *unreported is not zero*). Absent ⇒ no chip and no claim, never a fabricated band.
   */
  severity?: NewsSeverity;
}

/**
 * Mirror of the route's `NewsBody` — field-for-field, not extended from `FeedMeta`, because
 * this envelope genuinely differs (CvdResponse precedent): `vendor_ts` is NULLABLE (64 of
 * 1,007 good snapshots carry none), `age_s` is measured from `receipt_ts` BY DESIGN (the
 * route's own comment: vendor_ts is the newest HEADLINE's stamp, ~35m behind receipt on a
 * quiet feed — ageing a 2-minute budget against it would read STALE on a healthy feed).
 */
/** Mirror of the route's `DayClass` (workers/read-api/src/news.ts:320). */
export type DayClass = "weekday" | "weekend";

export interface NewsResponse {
  ok: true;
  endpoint: string;
  root: string;
  as_of: string;
  vendor_ts: string | null;
  receipt_ts: string;
  poll_seq: number;
  age_s: number;
  budget_s: number;
  stale: boolean;
  staleness_chip: "LIVE" | "STALE" | (string & {});
  union_complete: boolean;
  union_half: string;
  union_missing: string[];
  union_note: string;
  semantic: string;
  relevance_mode: string;
  relevance_is_heuristic: boolean;
  filters_applied: Record<string, unknown>;
  /*
   * ⛔ R15b: `measured_yield_per_day` + `measured_yield_window` are RETIRED — declaration removed
   * here BECAUSE the route no longer serves them (same commit). The old comment kept the field
   * declared "only because the route still serves it"; that premise is now false, and a client
   * type outliving its producer is how a dead field gets re-read.
   * The defect GK-gg measured (`f9e9aff7`) was the panel READING the blended key — `?? 19.7` was
   * never the mechanism. Use `measured_yield_applicable`, below, with its basis.
   */
  /**
   * BUILDER-yy's day-class yield (`6a80df5`, served ver `b8234de3`) — the number that APPLIES
   * to the day being rendered, and the basis that qualifies it.
   *
   * ⛔ WHY THE BLENDED MAP IS NOT ENOUGH, MEASURED ON THE SERVED ROUTE. The rail served
   * `measured_yield_per_day.is_major_and_index_or_macro = 19.7` on a SUNDAY whose applicable
   * yield is 1.25 — so the panel quoted a weekday-blended constant beside 0 items and called
   * an empty weekend normal for the wrong reason. yy measured the split: weekday 26.62,
   * weekend 1.25. 19.7 is right on NO day.
   * ⇒ ***A YIELD WITHOUT ITS DAY-CLASS IS A NUMBER LOOKING FOR A DAY THAT DOES NOT EXIST.***
   *
   * OPTIONAL (x's call, kept on merge) because a route predating the ship serves neither.
   * Absent ⇒ the panel says so; it does NOT fall back to the blended map, which is the
   * defect one layer down. `null` = measured and NOT APPLICABLE — also render no number.
   */
  measured_yield_applicable?: number | null;
  /** The filter, day-class and measurement window in the route's own words. Rendered verbatim. */
  measured_yield_basis?: string | null;
  /** Open arm (`SeverityBand` precedent): an unheard day-class renders as its own words. */
  day_class?: DayClass | (string & {}) | null;
  /**
   * Provenance for `measured_yield_applicable`. ⚠ NOT `measured_yield_window` — the two are
   * DIFFERENT strings on the live payload (…08-09T22:00Z vs …08-16T17:59Z, measured 21:0xZ,
   * confirmed at GK-gg's hand e5a57d34). Pairing the applicable number with the blended
   * window is the same defect wearing a different field name, so the pairing is ARMED.
   * Optional for the same reason as the fields above: an older route serves none of them.
   */
  measured_yield_by_day_class_window?: string | null;
  count: number;
  dropped_items: number;
  /** `"OURS"` today — the attribution the chips must carry. Optional: routes predate it. */
  severity_source?: string;
  /** The scoring recipe in the route's own words, rendered rather than paraphrased. */
  severity_basis?: string;
  items: NewsItem[];
}

/**
 * The capsule's budget, duplicated from `workers/read-api/src/news.ts` (NEWS_BUDGET_S)
 * ONLY as the chip's fallback before the first payload arrives — every real verdict uses
 * `payload.budget_s`, the route's own number, never this constant.
 */
export const NEWS_FALLBACK_BUDGET_S = 120;

/** What the fetch layer hands over on failure. `FeedError`'s shape, decoupled for tests. */
export interface FeedFailure {
  status: number;
  message: string;
  detail?: string | null;
}

export type NewsFeedState =
  | { kind: "no-snapshot"; reason: string }
  | { kind: "error"; message: string; detail: string | null }
  | { kind: "empty"; payload: NewsResponse }
  | { kind: "present"; payload: NewsResponse };

/**
 * Classify one fetch outcome into the state the panel renders. Returns `null` only before
 * the first fetch resolves (the "Loading…" render, which is not a feed state).
 *
 * ⛔ THE 404 TEST IS ON `status`, NEVER ON MESSAGE TEXT: the route's 404 body says
 * "no news snapshot exists strictly before T" and that reason is rendered VERBATIM, but
 * matching prose to detect the state would break the day the server rewords itself.
 */
export function classifyNewsFeed(
  payload: NewsResponse | null,
  failure: FeedFailure | null,
): NewsFeedState | null {
  if (failure !== null) {
    if (failure.status === 404) return { kind: "no-snapshot", reason: failure.message };
    return { kind: "error", message: failure.message, detail: failure.detail ?? null };
  }
  if (payload === null) return null;
  if (payload.items.length === 0) return { kind: "empty", payload };
  return { kind: "present", payload };
}

/**
 * Is this payload STALE-SHAPED? `stale === true` OR `age_s > budget_s` — the OR is
 * deliberate belt-and-braces: the server computes `stale := age_s > NEWS_BUDGET_S`, so the
 * two legs agree on every honest payload, and a payload where they DISAGREE is a payload
 * something rewrote — which must fail LOUD (law 1), never quiet.
 *
 * Drives the list dimming AND (via `newsChipInputs`) the chip. This is the render half of
 * SPX-044 clause (4).
 */
/**
 * The keyed-each identity for a headline row (Codex P2 finding ④, driven by HUDCHAT-s 2026-08-15).
 *
 * ⛔ THE SHIPPED KEY WAS `created_at + headline`. Driven: the SAME story carried by two wires at the
 * same second collapses to ONE key — 1 distinct of 2 — and Svelte's keyed-each reconciliation is
 * undefined on a duplicate.
 * ⭐ AND THE FIX I DID NOT SHIP, RECORDED BECAUSE IT IS THE TEMPTING ONE: adding the loop INDEX
 * guarantees uniqueness and is WRONG. This list is newest-first and new items PREPEND, so every
 * index shifts on every poll — every key changes, the whole list re-renders, and the key stops
 * being an identity at all. ⇒ ***A KEY THAT INCLUDES POSITION IS NOT A KEY.***
 * `source` is the discriminator that actually separates the colliding case, and NUL-joining keeps
 * "ab"+"c" from colliding with "a"+"bc".
 */
/**
 * ⭐ THE SINGLE STALENESS PREDICATE (Codex P2 ③; GK-aa's bar `0da47121` ③: *"chip and list agree
 * from ONE predicate"*).
 *
 * ⛔ The panel used to dim on `newsStaleShaped(payload)` — the payload's FROZEN served age — while
 * the chip beside it rendered `newsChipInputs(payload, elapsedS)`, which adds local drift. Driven:
 * age 100 of a 120 budget + 90s drift ⇒ ** the chip read STALE while every headline stayed fully
 * opaque. ** ⇒ ***TWO SURFACES ON ONE PANEL DISAGREEING ABOUT THE SAME FEED IS WORSE THAN EITHER
 * BEING WRONG ALONE: the chip says do not trust this, the list says it is fine.***
 *
 * ⇒ This derives FROM `newsChipInputs`, so it is not a second opinion that happens to agree —
 * ***the two CANNOT diverge, because there is only one comparison.*** An `||` of two independent
 * tests would have been a coincidence maintained by hand.
 */
export function newsStale(
  p: Pick<NewsResponse, "stale" | "age_s" | "budget_s">,
  elapsedS: number,
): boolean {
  if (p.stale === true) return true;
  const chip = newsChipInputs(p, elapsedS);
  return chip.ageS > chip.budgetS;
}

export function newsItemKey(
  item: Pick<NewsItem, "created_at" | "headline"> & { source?: string | null },
): string {
  return [item.created_at, item.source ?? "", item.headline].join("\u0000");
}

export function newsStaleShaped(p: Pick<NewsResponse, "stale" | "age_s" | "budget_s">): boolean {
  return p.stale === true || p.age_s > p.budget_s;
}

/**
 * The chip's inputs, derived from the payload's OWN fields plus the seconds elapsed since
 * WE received it — the served `age_s` is frozen at serve time, and a panel polling every
 * 60s that renders it raw would show a 40s-old number as 40s old for a full minute. The
 * elapsed term keeps the chip's clock honest between polls (isStillFresh precedent).
 *
 * ⛔ THE PANEL MUST HAND THESE TO StalenessChip WITH **NO `lastBarMs`** — that prop opens
 * the CLOSED escape hatch, and news has no market session: the capture lane polls around
 * the clock and `age_s` is receipt-based, so there is no shut-market state to excuse an
 * over-budget snapshot. With no `lastBarMs` and no error, over-budget renders exactly one
 * way: the LOUD `.stale` branch. (StalenessChip's own comment: "the dealer rail's chip …
 * passes no lastBarMs, so it cannot reach the closed branches at all.")
 */
export function newsChipInputs(
  p: Pick<NewsResponse, "age_s" | "budget_s">,
  elapsedS: number,
): { ageS: number; budgetS: number } {
  return {
    ageS: p.age_s + Math.max(0, elapsedS),
    budgetS: p.budget_s,
  };
}

/**
 * The union caveat, DATA-DRIVEN so it self-retires (D-3 precedent: the dealer rail's
 * root caveat disappears by itself the day the feed is fixed, instead of surviving as a
 * lie). The route serves `union_complete: false` + `union_missing: ["BABEL"]` in the BODY
 * precisely so consumers render it; a rail that shows the UW half and calls itself the
 * capsule's news layer is SPX-042's half-a-ratio in a new coat.
 */
export function unionCaveat(
  p: Pick<NewsResponse, "union_complete" | "union_half" | "union_missing">,
): string | null {
  if (p.union_complete && p.union_missing.length === 0) return null;
  const missing = p.union_missing.length > 0 ? p.union_missing.join(" + ") : "an unnamed half";
  return `${p.union_half} half only — ${missing} absent`;
}

/**
 * A headline's age from its own `created_at`. NaN → null, NEVER a fabricated number: a
 * headline that cannot date itself gets an em-dash, not an age (headlinePrice ① precedent).
 * The route already drops unparseable `created_at` rows server-side (`dropped_items`), so
 * this branch is belt-and-braces against a payload this client has not heard before.
 */
export function headlineAgeS(createdAt: string, nowMs: number): number | null {
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, (nowMs - t) / 1000);
}

/**
 * The severity chip for one headline, or `null` when the route served none.
 *
 * ⛔ WHY THIS IS A FUNCTION AND NOT A TERNARY IN THE TEMPLATE: `.svelte` is out of the
 * suite's reach (M10 — this repo has no component-render harness), so a decision left in
 * markup kills no arm. The `CVD (session)` mislabel survived for the lane's whole life in
 * exactly that position. ***Extract the value so it can be driven.***
 *
 * Fail-LOUD on a malformed score: a NaN coerced into a chip renders a confident-looking
 * number that means nothing, which is the failure mode this whole surface exists to avoid.
 */
export function severityChip(
  item: Pick<NewsItem, "severity">,
): { band: string; score: number; title: string } | null {
  const sev = item.severity;
  if (sev === undefined || sev === null) return null; // absence, stated by rendering nothing
  if (!Number.isFinite(sev.score)) {
    throw new Error(`severityChip: severity.score must be finite; got ${sev.score}`);
  }
  const band = String(sev.band ?? "").trim() || "unbanded";
  // The title carries OURS + the served basis. A reader hovering must never be able to
  // mistake this for the vendor's classification.
  return { band, score: sev.score, title: `severity ${sev.score} (${band}) — ${sev.basis}` };
}

/**
 * The panel's one-line attribution for the severity column.
 *
 * ⚠ `severity_source` is served as `"OURS"`. Rendering the chips WITHOUT this line is the
 * mislabel defect in its purest form: a computed stopgap sitting in a column of vendor
 * facts. If the source is ever absent, we say UNATTRIBUTED rather than assume it is ours.
 */
export function severityAttribution(source: string | null | undefined): string {
  const s = (source ?? "").trim();
  if (!s) return "severity UNATTRIBUTED — source not stated by the route";
  if (s === "OURS") return "severity is OURS (stopgap, not the vendor's, not BABEL)";
  return `severity source: ${s}`;
}

/**
 * The empty-state's YIELD CLAIM, as data rather than as template prose.
 *
 * The defect this replaces (GK-gg `f9e9aff7`): the panel read the BLENDED
 * `measured_yield_per_day.is_major_and_index_or_macro` and painted "~19.7/day" on a
 * weekend where the route served 1.25 — SHIPPED was true of the route and false of the
 * sentence the owner read. Extracted to a pure function so the claim can be driven by
 * arms; a sentence assembled inline in the template is a sentence nobody can test.
 *
 * `null` return = the route measured NOTHING for this filter+day-class, and the panel
 * must then say so in words and render NO figure. That is the route's own contract
 * (`measured_yield_applicable: null` ⇒ "render no number"), and it is why there is no
 * default anywhere in this function: a fallback is exactly how 19.7 survived.
 */
export function newsYieldClaim(
  p: Pick<
    NewsResponse,
    "measured_yield_applicable" | "measured_yield_basis" | "day_class" | "measured_yield_by_day_class_window"
  >,
): { perDay: number; dayClass: string; window: string; basis: string } | null {
  // typeof === "number", NOT a truthiness or ?? test: a SERVED 0 is a measurement ("this
  // filter yields nothing on this day-class") and must render as 0, not be swallowed as
  // absent. x's call on the original object, kept on merge.
  if (typeof p.measured_yield_applicable !== "number") return null;
  return {
    perDay: p.measured_yield_applicable,
    // An absent/unheard day-class renders as an em-dash rather than inventing "weekday":
    // the number is still true, only its day-class is unstated.
    dayClass: (p.day_class ?? "—") as string,
    // The BY-DAY-CLASS window, never `measured_yield_window`: the latter is the blended
    // field's provenance and the two are different strings on the live payload. Pairing a
    // day-class number with the blended window would re-tell the same lie in provenance.
    window: p.measured_yield_by_day_class_window ?? "an unstated window",
    basis: p.measured_yield_basis ?? "",
  };
}
